import type { CatalogProduct } from "@shared/types";
import { formatPaise } from "@/lib/format";

/**
 * What to print in a product row's price cell.
 *
 * ONE SOURCE, AND A DASH WHEN IT IS SILENT.
 *
 * `price` is the projection (`catalog_price_current`), GST-normalised so two
 * products are actually comparable. Absence is a FIRST-CLASS state: a labour
 * line, or a SKU nobody has priced, renders as a dash.
 *
 * The first version fell back to `indicativePriceMin` when the projection was
 * silent, and CI's price-source ratchet rejected it -- correctly. That column is
 * the legacy mirror plan 004 replaced: a raw published number of unknown basis,
 * so falling back to it lets one screen answer "what does this cost" two ways,
 * roughly 18% apart. A dash says "we do not know", which is true; an indicative
 * floor says "about this much", which nobody can stand behind.
 */
export function priceLabel(
	product: Pick<CatalogProduct, "price" | "prices">,
): string {
	if (!product.price) return "\u2014";

	/*
	 * "From" when the product has SEVERAL projected prices.
	 *
	 * `headlinePrice` picks the cheapest of them, so a tile sold in four sizes
	 * printed its smallest size's figure with nothing marking it as the floor of
	 * a range. An operator comparing that against a single-variant product two
	 * rows down is comparing a floor against a price -- the same
	 * not-actually-comparable trap the GST normalisation exists to close, one
	 * level up.
	 */
	const amount = formatPaise(product.price.amount);
	return (product.prices?.length ?? 0) > 1 ? `from ${amount}` : amount;
}

/**
 * Whether the projection considers this price past its refresh cadence.
 *
 * Surfaced beside the number rather than folded into it, because a stale price
 * is still the best number available -- it is old, not wrong. Printing it plain
 * implies a currency it does not have; hiding it removes the only figure an
 * operator has. Plan 008's dashboard is meant to queue these up, and this is the
 * same fact one row at a time.
 */
export function isPriceStale(product: Pick<CatalogProduct, "price">): boolean {
	return product.price?.isStale === true;
}

/**
 * A unit of measure, as a person would read it.
 *
 * `sq_ft` is the API's enum member, not a label -- it is what the Postgres enum
 * and the Zod enum both hold, and it is not something to show an operator. This
 * lives here rather than beside the one screen that first needed it because a
 * second screen now needs it too, and a second copy of a formatter is how this
 * codebase ended up with three money formatters disagreeing about rounding.
 */
export function uomLabel(uom: string): string {
	return uom.replace(/_/g, " ");
}
